import { observable, when } from '@legendapp/state';

import { onZoomWheel } from '../../components/zoom-controls/utils/on-zoom-wheel';

import { ProductView, TrackingEventName, ZoomActionTypes } from './tracking/types';

import { dragOverlayStoreActions } from './drag-overlay-store';
import { featureAppStore } from './feature-app-store';
import { interiorStore } from './interior-store';
import { trackingStore } from './tracking-store';

const HEADER_HEIGHT = 52;

interface ContainerStore {
	container: HTMLElement | null;
	isContainerReady: boolean;
	windowInnerHeight: number;
	headerHeight: number;
	nbaHeight: number;
	combinedHeight: number;
	bottomPanelExpansion: number;
}

export const containerStore = observable<ContainerStore>({
	container: null,
	isContainerReady: false,
	windowInnerHeight: () => window.innerHeight,

	headerHeight: HEADER_HEIGHT,

	nbaHeight: () => {
		return getNbaHeight();
	},
	combinedHeight: () => HEADER_HEIGHT + getNbaHeight(),
	bottomPanelExpansion: 0
});

export const containerStoreActions = {
	setBottomPanelExpansion: (heightPx: number) => {
		containerStore.bottomPanelExpansion.set(heightPx);
	}
};

function getNbaHeight() {
	const { featureServices, featureAppId } = featureAppStore.get();

	if (!featureServices || !featureAppId) {
		return 0;
	}

	const nbabInfo = featureServices['cms-service'].getNbabInfo(featureAppId);
	const isHorizontalNbab = nbabInfo?.nbabType === 'horizontal';

	// Local element doesn't use service
	const localNbaElement = document
		.querySelector('[class^="NextBestActionGroup__StyledNextBestActionPanelWrapper"]')
		?.getBoundingClientRect().height;

	let nbaElement = nbabInfo?.boundingElement?.getBoundingClientRect().height || localNbaElement;

	const nbaHeight = isHorizontalNbab ? nbaElement || 0 : 0;
	return nbaHeight;
}

// This is pinch gesture
when(
	() => containerStore.container.get(),
	() => {
		const container = containerStore.container.get() as HTMLElement;
		const isInteriorLoaded = interiorStore.isInteriorLoaded.get();

		let gestureState = {
			isActive: false,
			pointers: new Map<number, PointerEvent>()
		};

		const onPinch = (delta: number) => {
			trackingStore.setTrackingTimer('VISUALIZER_VEHICLE_ZOOM', TrackingEventName.VISUALIZER_VEHICLE_ZOOM, {
				contentId: ZoomActionTypes.SCROLL,
				sectionName: isInteriorLoaded ? ProductView.INTERIOR_VIEW : ProductView.EXTERNAL_VIEW
			});

			dragOverlayStoreActions.setIsDragOverlayVisible(false);

			return onZoomWheel(delta);
		};

		const handlePointerDown = (e: PointerEvent) => {
			gestureState.pointers.set(e.pointerId, e);

			if (gestureState.pointers.size === 2) {
				gestureState.isActive = true;
			}
		};

		const handlePointerMove = (e: PointerEvent) => {
			if (!gestureState.isActive || gestureState.pointers.size !== 2) return;

			const prevPointer = gestureState.pointers.get(e.pointerId);
			if (!prevPointer) return;

			gestureState.pointers.set(e.pointerId, e);
			const pointers = Array.from(gestureState.pointers.values()) as [PointerEvent, PointerEvent, ...PointerEvent[]];

			if (pointers.length !== 2) return;

			const getDistance = ([x1, y1]: [number, number], [x2, y2]: [number, number]) => Math.hypot(x1 - x2, y1 - y2);

			const prevDistance = getDistance(
				[prevPointer.pageX, prevPointer.pageY],
				[
					pointers.find(p => p.pointerId !== e.pointerId)?.pageX || 0,
					pointers.find(p => p.pointerId !== e.pointerId)?.pageY || 0
				]
			);
			const newDistance = getDistance([pointers[0].pageX, pointers[0].pageY], [pointers[1].pageX, pointers[1].pageY]);

			onPinch(prevDistance - newDistance);
			e.preventDefault();
		};

		const handlePointerUp = (e: PointerEvent) => {
			gestureState.pointers.delete(e.pointerId);

			if (gestureState.pointers.size < 2) {
				gestureState.isActive = false;
			}
		};

		// iOS Safari requires passive: false for preventDefault to work
		const eventOptions = { passive: false };

		container.addEventListener('pointerdown', handlePointerDown, eventOptions);
		container.addEventListener('pointermove', handlePointerMove, eventOptions);
		container.addEventListener('pointerup', handlePointerUp, eventOptions);
		container.addEventListener('pointercancel', handlePointerUp, eventOptions);

		return () => {
			container.removeEventListener('pointerdown', handlePointerDown);
			container.removeEventListener('pointermove', handlePointerMove);
			container.removeEventListener('pointerup', handlePointerUp);
			container.removeEventListener('pointercancel', handlePointerUp);
		};
	}
);
